import { useState, useRef } from "react";
import { Loader2, Shield, CheckCircle2, Upload, X, AlertCircle } from "lucide-react";

const MAX_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function FileDrop({ label, file, setFile, testId }) {
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  const onFile = (f) => {
    setError('');
    if (!f) return;
    if (!ALLOWED.includes((f.type || '').toLowerCase())) {
      setError('Only JPG, PNG, WEBP allowed');
      return;
    }
    if (f.size > MAX_SIZE) {
      setError('Max 5MB per image');
      return;
    }
    setFile(f);
  };

  const preview = file ? URL.createObjectURL(file) : null;

  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">{label}</label>
      {!file ? (
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50/50 transition-colors"
          data-testid={`${testId}-drop`}
        >
          <Upload className="h-7 w-7 text-slate-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-700">Tap to upload</p>
          <p className="text-xs text-slate-500 mt-1">JPG / PNG / WEBP &middot; up to 5 MB</p>
        </div>
      ) : (
        <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
          <img src={preview} alt={label} className="w-full h-40 object-cover" data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setFile(null); }}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-black"
            data-testid={`${testId}-remove`}
          >
            <X className="h-4 w-4" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[11px] px-3 py-1.5 truncate">
            {file.name} &middot; {(file.size / 1024).toFixed(0)} KB
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {error && (
        <p className="mt-2 text-xs text-red-600 flex items-center gap-1" data-testid={`${testId}-error`}>
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
}

export default function AadhaarStep({ loading, onUpload, rejectionReason }) {
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [numError, setNumError] = useState('');

  const canSubmit = front && back && aadhaarNumber.replace(/\s/g, '').length === 12;

  const handleSubmit = (e) => {
    e.preventDefault();
    const clean = aadhaarNumber.replace(/\s/g, '');
    if (!/^\d{12}$/.test(clean)) {
      setNumError('Aadhaar must be exactly 12 digits');
      return;
    }
    setNumError('');
    onUpload({ aadhaar_number: clean, aadhaar_front: front, aadhaar_back: back });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="aadhaar-step-form">
      {/* Intro */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900 text-sm">Upload Aadhaar for KYC</h3>
            <p className="text-xs text-blue-800 mt-1 leading-relaxed">
              Upload clear photos of the <strong>front and back</strong> of your Aadhaar card. Our team will verify them — usually within a few hours.
            </p>
          </div>
        </div>
      </div>

      {/* Rejection notice */}
      {rejectionReason && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-2" data-testid="aadhaar-rejection-notice">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
          <div className="text-xs text-red-900">
            <strong>Your previous upload was rejected.</strong>
            <p className="mt-1">{rejectionReason}</p>
          </div>
        </div>
      )}

      {/* Aadhaar number */}
      <div>
        <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">12-digit Aadhaar number</label>
        <input
          type="text"
          inputMode="numeric"
          maxLength={14}
          value={aadhaarNumber}
          onChange={(e) => {
            // format as "XXXX XXXX XXXX"
            const digits = e.target.value.replace(/\D/g, '').slice(0, 12);
            const parts = [digits.slice(0, 4), digits.slice(4, 8), digits.slice(8, 12)].filter(Boolean);
            setAadhaarNumber(parts.join(' '));
            if (numError) setNumError('');
          }}
          placeholder="XXXX XXXX XXXX"
          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-base tracking-wider font-mono"
          data-testid="aadhaar-number-input"
        />
        {numError && (
          <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="aadhaar-number-error">
            <AlertCircle className="h-3 w-3" /> {numError}
          </p>
        )}
      </div>

      {/* Front + Back uploads */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FileDrop label="Aadhaar — FRONT" file={front} setFile={setFront} testId="aadhaar-front" />
        <FileDrop label="Aadhaar — BACK" file={back} setFile={setBack} testId="aadhaar-back" />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={!canSubmit || loading}
        className="btn-gradient w-full h-14 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="aadhaar-submit-btn"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            <Upload className="h-5 w-5" />
            Submit for verification
          </>
        )}
      </button>

      {/* Security note */}
      <div className="bg-green-50/50 border border-green-200 rounded-xl p-3 flex items-start gap-2">
        <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-green-900">
          Your documents are stored securely and only reviewed by our verified team.
        </p>
      </div>
    </form>
  );
}
